import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, 
  Lightbulb, 
  Wand2, 
  Palette, 
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Navbar from '../components/layout/Navbar';

const resources = [
  {
    title: 'Getting Started Guides',
    description: 'Step-by-step guides to help you launch your first campaigns and master the platform basics.',
    icon: BookOpen,
    color: 'from-blue-500 to-cyan-500',
    items: [
      'Platform Setup & Onboarding',
      'Creating Your First Campaign',
      'Connecting Social Accounts',
      'Understanding the Dashboard'
    ]
  },
  {
    title: 'Content Creation Tips',
    description: 'Expert strategies and creative techniques to make your content stand out and engage audiences.',
    icon: Lightbulb,
    color: 'from-yellow-500 to-orange-500',
    items: [
      'Visual Design Best Practices',
      'Writing Compelling Captions',
      'Choosing the Right Content Types',
      'Seasonal Content Planning'
    ]
  },
  {
    title: 'AI Prompting Guide',
    description: 'Master the art of AI prompting to generate exactly the content you envision with our AI tools.',
    icon: Wand2,
    color: 'from-purple-500 to-pink-500',
    items: [
      'Effective Prompt Structure',
      'Style and Mood Keywords',
      'Product Photography Prompts',
      'Advanced Prompting Techniques'
    ]
  },
  {
    title: 'Brand Guidelines',
    description: 'Learn how to maintain consistent brand identity across all your social media content.',
    icon: Palette,
    color: 'from-green-500 to-teal-500',
    items: [
      'Setting Up Your Brand Kit',
      'Color Palette Management',
      'Logo and Asset Guidelines',
      'Brand Voice Consistency'
    ]
  },

];

const ResourcesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <Navbar />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #30b3dd 0%, #4a298a 50%, #30b3dd 100%)' }}>
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold" style={{ color: '#2a9cc7' }}>
                Resources
              </h1>
            </div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Everything you need to master social media marketing with AI-powered tools and proven strategies.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {resources.map((resource, index) => {
            const Icon = resource.icon;
            return (
              <motion.div
                key={resource.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                  <CardHeader>
                    <div className={`w-12 h-12 bg-gradient-to-r ${resource.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-gray-900">
                      {resource.title}
                    </CardTitle>
                    <CardDescription className="text-gray-600">
                      {resource.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {resource.items.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-center text-sm text-gray-700">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3 flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                    <Link to={`/resources/${resource.title.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}>
                      <Button 
                        variant="outline" 
                        className="w-full group-hover:bg-gray-50 transition-colors duration-300"
                      >
                        Explore Guide
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section - Dark Mode */}
        <section className="py-12 sm:py-16 md:py-20 bg-slate-900 rounded-2xl mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="hero-title text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              <span style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Ready to Get Started?
              </span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed">
              Put these resources into action and start creating amazing social media content today.
            </p>

            <div className="flex justify-center px-4 sm:px-0">
              <Link to="/register" className="w-full sm:w-auto max-w-sm sm:max-w-none">
                <motion.div
                  whileHover={{ scale: 1.05, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative"
                >
                  <div className="absolute -inset-1 rounded-2xl blur opacity-30 group-hover:opacity-70 transition duration-500" style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a, #4a298a)' }}></div>
                  <Button
                    size="lg"
                    className="relative w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-lg sm:text-xl font-semibold shadow-lg hover:shadow-2xl rounded-2xl btn-interactive ripple overflow-hidden text-white"
                    style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
                  >
                    <span className="relative z-10">Start Free</span>
                    <ArrowRight className="ml-3 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-300 relative z-10" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ResourcesPage;